import React, { useState, useEffect, useRef } from 'react';
import './CustomColorPicker.css';

const CustomColorPicker = ({ color, onChange, onClose, onColorSelect, onEyedropperActive }) => {
  const [hue, setHue] = useState(0);
  const [saturation, setSaturation] = useState(100);
  const [lightness, setLightness] = useState(50);
  const [isDraggingWheel, setIsDraggingWheel] = useState(false);
  const [isDraggingLightness, setIsDraggingLightness] = useState(false);
  const [isEyedropperMode, setIsEyedropperMode] = useState(false);
  
  const wheelRef = useRef(null);
  const lightnessBarRef = useRef(null);
  const canvasRef = useRef(null);
  
  // Refs pour stocker les valeurs actuelles sans déclencher de re-renders
  const hueRef = useRef(0);
  const saturationRef = useRef(100);
  const lightnessRef = useRef(50);
  const onChangeRef = useRef(onChange);
  
  // Mettre à jour les refs quand les valeurs changent
  useEffect(() => {
    hueRef.current = hue;
    saturationRef.current = saturation;
    lightnessRef.current = lightness;
  }, [hue, saturation, lightness]);
  
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Convertir hex en HSL au chargement
  useEffect(() => {
    if (color) {
      const hsl = hexToHSL(color);
      setHue(hsl.h);
      setSaturation(hsl.s);
      setLightness(hsl.l);
      hueRef.current = hsl.h;
      saturationRef.current = hsl.s;
      lightnessRef.current = hsl.l;
    }
  }, [color]);

  // Convertir HSL en hex
  const hslToHex = (h, s, l) => {
    l /= 100;
    const a = s * Math.min(l, 1 - l) / 100;
    const f = n => {
      const k = (n + h / 30) % 12;
      const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * color).toString(16).padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`;
  };

  // Convertir hex en HSL
  const hexToHSL = (hex) => {
    let r = 0, g = 0, b = 0;
    if (hex.length === 7) {
      r = parseInt(hex.slice(1, 3), 16) / 255;
      g = parseInt(hex.slice(3, 5), 16) / 255;
      b = parseInt(hex.slice(5, 7), 16) / 255;
    }
    
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
        default: break;
      }
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100)
    };
  };

  // Dessiner le cercle chromatique sur le canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const size = canvas.width;
    const center = size / 2;
    const radius = center - 10;
    
    ctx.clearRect(0, 0, size, size);
    
    const imageData = ctx.createImageData(size, size);
    const data = imageData.data;
    
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const dx = x - center;
        const dy = y - center;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance <= radius) {
          let angle = Math.atan2(dy, dx) * 180 / Math.PI + 90;
          if (angle < 0) angle += 360;
          
          const saturation = (distance / radius) * 100;
          const lightness = 50;
          
          const hslToRgb = (h, s, l) => {
            h /= 360;
            s /= 100;
            l /= 100;
            let r, g, b;
            
            if (s === 0) {
              r = g = b = l;
            } else {
              const hue2rgb = (p, q, t) => {
                if (t < 0) t += 1;
                if (t > 1) t -= 1;
                if (t < 1/6) return p + (q - p) * 6 * t;
                if (t < 1/2) return q;
                if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
                return p;
              };
              
              const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
              const p = 2 * l - q;
              r = hue2rgb(p, q, h + 1/3);
              g = hue2rgb(p, q, h);
              b = hue2rgb(p, q, h - 1/3);
            }
            
            return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
          };
          
          const [r, g, b] = hslToRgb(angle, saturation, lightness);
          const index = (y * size + x) * 4;
          
          data[index] = r;
          data[index + 1] = g;
          data[index + 2] = b;
          data[index + 3] = 255;
        }
      }
    }
    
    ctx.putImageData(imageData, 0, 0);
    
    ctx.fillStyle = 'white';
    ctx.beginPath();
    ctx.arc(center, center, radius * 0.1, 0, 2 * Math.PI);
    ctx.fill();
  }, []);

  // Gérer le mouvement sur le cercle chromatique
  const handleWheelMove = (e) => {
    const rect = wheelRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const x = e.clientX - centerX;
    const y = e.clientY - centerY;
    
    const distance = Math.sqrt(x * x + y * y);
    const canvasSize = Math.min(rect.width, rect.height);
    const radius = (canvasSize / 2) * 0.92;
    
    if (distance <= radius) {
      let angle = Math.atan2(y, x) * 180 / Math.PI + 90;
      if (angle < 0) angle += 360;
      
      const newSaturation = Math.min(100, (distance / radius) * 100);
      const hexColor = hslToHex(angle, newSaturation, lightnessRef.current);
      
      setHue(angle);
      setSaturation(newSaturation);
      if (onChangeRef.current) {
        onChangeRef.current({ hex: hexColor });
      }
    }
  };

  // Gérer le mouvement sur la barre de luminosité
  const handleLightnessBarMove = (e) => {
    const rect = lightnessBarRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const newLightness = (x / rect.width) * 100;
    
    setLightness(newLightness);
    const hexColor = hslToHex(hueRef.current, saturationRef.current, newLightness);
    if (onChangeRef.current) {
      onChangeRef.current({ hex: hexColor });
    }
  };

  // Événements de souris pour le cercle chromatique - sans dépendances pour éviter les re-créations
  useEffect(() => {
    if (!isDraggingWheel) return;
    
    const handleMouseMove = (e) => {
      handleWheelMove(e);
    };
    const handleMouseUp = () => setIsDraggingWheel(false);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingWheel]);

  // Événements de souris pour la barre de luminosité - sans dépendances
  useEffect(() => {
    if (!isDraggingLightness) return;
    
    const handleMouseMove = (e) => {
      handleLightnessBarMove(e);
    };
    const handleMouseUp = () => setIsDraggingLightness(false);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingLightness]);

  const currentColor = hslToHex(hue, saturation, lightness);

  const handleEyedropperToggle = () => {
    const newMode = !isEyedropperMode;
    setIsEyedropperMode(newMode);
    if (onEyedropperActive) {
      onEyedropperActive(newMode);
    }
  };

  return (
    <div className="custom-color-picker">
      {/* Cercle chromatique */}
      <div
        ref={wheelRef}
        className="color-wheel-container"
        onMouseDown={(e) => {
          setIsDraggingWheel(true);
          handleWheelMove(e);
        }}
      >
        <canvas
          ref={canvasRef}
          className="color-wheel-canvas"
          width={250}
          height={250}
        />
        <div
          className="color-wheel-cursor"
          style={{
            left: `calc(50% + ${(saturation / 100) * 46 * Math.cos((hue - 90) * Math.PI / 180)}%)`,
            top: `calc(50% + ${(saturation / 100) * 46 * Math.sin((hue - 90) * Math.PI / 180)}%)`
          }}
        />
      </div>

      {/* Espace entre le cercle et la barre */}
      <div className="color-picker-spacer"></div>

      {/* Barre de luminosité */}
      <div
        ref={lightnessBarRef}
        className="color-lightness-bar"
        style={{
          background: `linear-gradient(to right, 
            hsl(${hue}, ${saturation}%, 0%), 
            hsl(${hue}, ${saturation}%, 50%), 
            hsl(${hue}, ${saturation}%, 100%))`
        }}
        onMouseDown={(e) => {
          setIsDraggingLightness(true);
          handleLightnessBarMove(e);
        }}
      >
        <div
          className="color-bar-cursor"
          style={{ 
            left: `${lightness}%`
          }}
        />
      </div>

      {/* Espace avant la prévisualisation */}
      <div className="color-picker-spacer"></div>

      {/* Prévisualisation de la couleur actuelle avec pipette */}
      <div className="color-preview-section">
        <div className="color-preview-compact" style={{ backgroundColor: currentColor }}>
          <span className="color-hex-text">{currentColor}</span>
        </div>
        <button 
          className={`color-eyedropper-btn ${isEyedropperMode ? 'active' : ''}`}
          onClick={handleEyedropperToggle}
          title={isEyedropperMode ? "Désactiver la pipette" : "Activer la pipette - Cliquez sur une lettre pour copier sa couleur"}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m2 22 1-1h3l9-9"/>
            <path d="M3 21v-3l9-9"/>
            <path d="m15 6 3.4-3.4a2.1 2.1 0 1 1 3 3L18 9l.4.4a2.1 2.1 0 1 1-3 3l-3.8-3.8a2.1 2.1 0 1 1 3-3l.4.4Z"/>
          </svg>
        </button>
      </div>

      {/* Boutons d'action */}
      <div className="color-picker-actions">
        <button 
          className="color-validate-btn"
          onClick={() => {
            if (onColorSelect) {
              onColorSelect({ hex: currentColor });
            }
          }}
        >
          Valider
        </button>
        <button 
          className="color-close-btn"
          onClick={onClose}
        >
          Fermer
        </button>
      </div>
    </div>
  );
};

export default CustomColorPicker;
